/**
 * Super Administrator — Staff Monitor (/super-admin/staff)
 *
 * Dashboard tiles + a server-paged staff table with live presence.
 *
 * Access: gated on the raw SuperAdmin claim. That gate is an affordance only — the real boundary is
 * [Authorize(Roles = SuperAdmin)] on the API. If a SystemAdmin reaches this URL, the page renders
 * "not permitted" because every fetch returns 403, which is the honest outcome.
 *
 * Live updates: the paged list is NOT refetched per SignalR event (40 employees beating every 60s
 * would mean a query every ~1.5s). Instead `useStaffMonitoringLive` supplies an overlay that patches
 * only the volatile columns — presence, current page, live session length.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useStaffMonitoringLive } from "@/hooks/use-staff-monitoring-live";
import {
  fetchStaffDashboard,
  fetchStaffDepartments,
  fetchStaffList,
  downloadStaffReport,
  type StaffListRow,
} from "@/lib/api/staff-monitoring";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { PaginationControls } from "@/components/PaginationControls";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableSkeleton } from "@/components/loading";
import { toast } from "sonner";
import {
  Activity,
  AlarmClock,
  ArrowUpDown,
  ChevronRight,
  Clock,
  Download,
  Moon,
  Radio,
  RotateCcw,
  Search,
  Trophy,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/super-admin/staff")({
  component: StaffMonitorPage,
});

const PRESENCE_STYLE: Record<string, { dot: string; text: string }> = {
  Online: { dot: "bg-green-500", text: "text-green-600" },
  Idle: { dot: "bg-amber-500", text: "text-amber-600" },
  Offline: { dot: "bg-muted-foreground/40", text: "text-muted-foreground" },
};

function PresenceDot({ presence }: { presence: string }) {
  const style = PRESENCE_STYLE[presence] ?? PRESENCE_STYLE.Offline;
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`h-2.5 w-2.5 rounded-full shrink-0 ${style.dot} ${
          presence === "Online" ? "animate-pulse" : ""
        }`}
      />
      <span className={`font-medium ${style.text}`}>{presence}</span>
    </span>
  );
}

function minutes(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value < 60) return `${value}m`;
  const h = Math.floor(value / 60);
  const m = value % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function StatTile({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{label}</p>
            {hint && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{hint}</p>}
          </div>
          <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function StaffMonitorPage() {
  const { isSuperAdmin, loading } = useAuth();

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [department, setDepartment] = useState("all");
  const [sortBy, setSortBy] = useState("status");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebouncedValue(search);

  // Any filter change invalidates the current page number — page 7 of an unfiltered list is rarely
  // a valid page of the filtered one.
  const filterKey = JSON.stringify({ debouncedSearch, role, status, department, sortBy, sortDir });
  useEffect(() => {
    setPageNumber(1);
  }, [filterKey]);

  const { live, connected } = useStaffMonitoringLive(isSuperAdmin);

  const dashboard = useQuery({
    queryKey: ["staff-monitoring", "dashboard"],
    queryFn: () => fetchStaffDashboard(),
    enabled: isSuperAdmin,
    // The tiles are aggregates; SignalR patches rows, not counts. A slow poll keeps them fresh
    // without turning the dashboard into a query loop.
    refetchInterval: 60_000,
  });

  const departments = useQuery({
    queryKey: ["staff-monitoring", "departments"],
    queryFn: fetchStaffDepartments,
    enabled: isSuperAdmin,
    staleTime: 5 * 60_000,
  });

  const staff = useQuery({
    queryKey: ["staff-monitoring", "staff", filterKey, pageNumber, pageSize],
    queryFn: () =>
      fetchStaffList({
        pageNumber,
        pageSize,
        search: debouncedSearch || undefined,
        role,
        status,
        department,
        sortBy,
        sortDir,
      }),
    enabled: isSuperAdmin,
  });

  // Merge the live overlay over the fetched page.
  const rows: StaffListRow[] = useMemo(() => {
    const items = staff.data?.items ?? [];
    return items.map((row) => {
      const patch = live[row.userId];
      if (!patch) return row;
      return {
        ...row,
        presence: patch.presence,
        presenceLabel: patch.presenceLabel,
        presenceDetail: patch.presence === "Online" ? "Online" : row.presenceDetail,
        currentPage: patch.currentPage ?? row.currentPage,
        lastSeenAt: patch.lastSeenAt ?? row.lastSeenAt,
        currentSessionMinutes:
          patch.sessionDurationSeconds != null
            ? Math.round(patch.sessionDurationSeconds / 60)
            : row.currentSessionMinutes,
      };
    });
  }, [staff.data, live]);

  const toggleSort = (column: string) => {
    if (sortBy === column) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(column);
      setSortDir("desc");
    }
  };

  const exportReport = async (
    kind: "sessions" | "activetime" | "connection" | "usage",
    format: "csv" | "excel" | "pdf",
  ) => {
    try {
      await downloadStaffReport(kind, format);
      toast.success(`${kind} report exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    }
  };

  if (loading) return null;
  if (!isSuperAdmin) {
    return (
      <AccessDenied message="Staff monitoring is limited to the Super Administrator. System Administrators, Team Leaders and Content Creators cannot view employee activity." />
    );
  }

  const d = dashboard.data;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Activity className="h-7 w-7 text-primary" />
            Staff Monitor
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Live activity for every employee — administrators, team leaders and content creators.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Honest connection indicator: if the live feed is down, the page says so rather than
              silently showing stale presence. */}
          <Badge variant={connected ? "secondary" : "outline"} className="gap-1.5 text-xs">
            <Radio
              className={`h-3 w-3 ${connected ? "text-green-600" : "text-muted-foreground"}`}
            />
            {connected ? "Live" : "Live feed offline"}
          </Badge>

          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to="/super-admin/leaderboard">
              <Trophy className="h-4 w-4" /> Leaderboard
            </Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">Sessions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportReport("sessions", "csv")}>
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("sessions", "excel")}>
                Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("sessions", "pdf")}>
                PDF
              </DropdownMenuItem>
              <DropdownMenuLabel className="text-xs">Active time</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportReport("activetime", "excel")}>
                Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("activetime", "csv")}>
                CSV
              </DropdownMenuItem>
              <DropdownMenuLabel className="text-xs">Connection status</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportReport("connection", "pdf")}>
                PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("connection", "csv")}>
                CSV
              </DropdownMenuItem>
              <DropdownMenuLabel className="text-xs">Page usage</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportReport("usage", "excel")}>
                Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tiles */}
      {dashboard.isError ? (
        <Card>
          <CardContent className="py-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              {(dashboard.error as Error)?.message ?? "Could not load the dashboard."}
            </p>
            <Button variant="outline" size="sm" onClick={() => dashboard.refetch()}>
              <RotateCcw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <StatTile
            label="Total staff"
            value={d?.totalStaff ?? "—"}
            icon={<Users className="h-4 w-4" />}
          />
          <StatTile
            label="Online now"
            value={d?.currentlyOnline ?? "—"}
            icon={<Radio className="h-4 w-4" />}
          />
          <StatTile
            label="Idle"
            value={d?.currentlyIdle ?? "—"}
            icon={<Clock className="h-4 w-4" />}
          />
          <StatTile
            label="Offline"
            value={d?.currentlyOffline ?? "—"}
            icon={<Users className="h-4 w-4" />}
          />
          <StatTile
            label="Avg daily activity"
            value={d ? minutes(Math.round(d.averageDailyActiveMinutes)) : "—"}
            hint="per employee / day"
            icon={<Activity className="h-4 w-4" />}
          />
          <StatTile
            label="Avg session"
            value={d ? minutes(Math.round(d.averageSessionMinutes)) : "—"}
            hint="completed sessions"
            icon={<Clock className="h-4 w-4" />}
          />
          <StatTile
            label="Logins today"
            value={d?.loginsToday ?? "—"}
            icon={<ChevronRight className="h-4 w-4" />}
          />
          <StatTile
            label="Logouts today"
            value={d?.logoutsToday ?? "—"}
            icon={<ChevronRight className="h-4 w-4" />}
          />
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="border-b bg-muted/20 py-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Employees</CardTitle>
              <CardDescription className="text-xs">
                {staff.data
                  ? `${staff.data.totalCount} employee(s) · page ${staff.data.pageNumber} of ${Math.max(1, staff.data.totalPages)} · message counts cover the same window as "Activity today"`
                  : "Loading…"}
              </CardDescription>
            </div>
          </div>

          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search name, email or job title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="SuperAdmin">Super Administrator</SelectItem>
                <SelectItem value="SystemAdmin">System Administrator</SelectItem>
                <SelectItem value="TeamLeader">Team Leader</SelectItem>
                <SelectItem value="ContentCreator">Content Creator</SelectItem>
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="idle">Idle</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>

            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger>
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {(departments.data ?? []).map((dep) => (
                  <SelectItem key={dep} value={dep}>
                    {dep}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {staff.isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={6} />
            </div>
          ) : staff.isError ? (
            <div className="p-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                {(staff.error as Error)?.message ?? "Could not load the staff list."}
              </p>
              <Button variant="outline" size="sm" onClick={() => staff.refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No employees match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {[
                      { key: "name", label: "Employee" },
                      { key: null, label: "Job title" },
                      { key: "department", label: "Department" },
                      { key: "status", label: "Status" },
                      { key: null, label: "Current page" },
                      { key: "activetime", label: "Activity today" },
                      { key: "sessions", label: "Sessions" },
                      { key: null, label: "Late replies (>24h)" },
                      { key: null, label: "After 4:30 PM" },
                      { key: "lastlogin", label: "Last login" },
                      { key: null, label: "Last logout" },
                      { key: null, label: "Current session" },
                      { key: null, label: "" },
                    ].map((col, i) => (
                      <th key={i} className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                        {col.key ? (
                          <button
                            className="inline-flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort(col.key!)}
                          >
                            {col.label}
                            <ArrowUpDown
                              className={`h-3 w-3 ${sortBy === col.key ? "text-primary" : "opacity-40"}`}
                            />
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((row) => (
                    <tr key={row.userId} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{row.fullName || row.email}</p>
                          <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {row.roles.map((r) => (
                              <Badge key={r} variant="outline" className="text-[10px]">
                                {r}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{row.jobTitle || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.department || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <PresenceDot presence={row.presence} />
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {row.presenceDetail}
                        </p>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        {row.currentPage ? (
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded truncate inline-block max-w-full">
                            {row.currentPage}
                          </code>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {minutes(row.activeMinutesInWindow)}
                      </td>
                      <td className="px-4 py-3">{row.sessionsInWindow}</td>
                      <td
                        className="px-4 py-3"
                        title="Messages this employee answered more than 24 hours after they were sent"
                      >
                        {row.lateRepliesInWindow > 0 ? (
                          <span className="inline-flex items-center gap-1 font-medium tabular-nums text-destructive">
                            <AlarmClock className="h-3.5 w-3.5" />
                            {row.lateRepliesInWindow}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3"
                        title="Messages this employee sent after 4:30 PM (Cairo time)"
                      >
                        {row.afterHoursMessagesInWindow > 0 ? (
                          <span className="inline-flex items-center gap-1 tabular-nums">
                            <Moon className="h-3.5 w-3.5 text-muted-foreground" />
                            {row.afterHoursMessagesInWindow}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {row.lastLogoutAt ? new Date(row.lastLogoutAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">{minutes(row.currentSessionMinutes)}</td>
                      <td className="px-4 py-3">
                        <Button asChild variant="ghost" size="sm" className="gap-1 text-xs">
                          <Link to="/super-admin/staff/$userId" params={{ userId: row.userId }}>
                            Details <ChevronRight className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {staff.data && staff.data.totalCount > 0 && (
            <div className="p-3 border-t bg-muted/10">
              <PaginationControls
                pageNumber={staff.data.pageNumber}
                pageSize={staff.data.pageSize}
                totalPages={Math.max(1, staff.data.totalPages)}
                totalCount={staff.data.totalCount}
                hasNextPage={staff.data.hasNextPage}
                hasPreviousPage={staff.data.hasPreviousPage}
                onPageChange={setPageNumber}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPageNumber(1);
                }}
                isLoading={staff.isFetching}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
